// src/pages/TrackCreation/LocationScreen.tsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocationScreenProps } from "../../../../types/screensTypes";
import Dropdown from "../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import TextInput from "../../../../components/ui/TextInput";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../context/useGlobalState";
import Button from "../../../../components/ui/Button";
import stepStyles from "../createStep.module.css";

const LocationScreen: React.FC<LocationScreenProps> = ({ navigateToNext }) => {
    const { data, loading, error } = useGlobalState();
    const { trackData, updateTrackData } = useTrackCreation();
    const [selectedCountry, setSelectedCountry] = useState<string | undefined>(trackData?.country);
    const [filteredCities, setFilteredCities] = useState<any[]>([]);
    const [coordinates, setCoordinates] = useState<string>("");
    const [hasLoadedCoordinates, setHasLoadedCoordinates] = useState<boolean>(false);
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    useEffect(() => {
        if (selectedCountry) {
            const citiesForCountry = data.locationCityOptions.filter((city: any) => city.countryId === selectedCountry);
            setFilteredCities(citiesForCountry);
        } else {
            setFilteredCities([]);
        }
    }, [selectedCountry, data.locationCityOptions]);

    useEffect(() => {
        if (trackData?.country != null) {
            setSelectedCountry(trackData.country);
        }
    }, [trackData?.country]);

    const updateCoordinatesStartingPoint = (coords: string): void => {
        setCoordinates(coords);
        if (!coords.trim()) {
            updateTrackData("startingLat", null);
            updateTrackData("startingLng", null);
            return;
        }
        const [latitude, longitude] = coords.split(",");
        if (latitude && longitude) {
            updateTrackData("startingLat", latitude.trim());
            updateTrackData("startingLng", longitude.trim());
        }
    };

    useEffect(() => {
        if (trackData?.startingLat && trackData?.startingLng && !hasLoadedCoordinates) {
            setHasLoadedCoordinates(true);
            setCoordinates(`${trackData.startingLat}, ${trackData.startingLng}`);
        }
    }, [trackData, hasLoadedCoordinates]);

    return (
        <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step}`}>
            <TitleSubtitle
                title={t("whereIsYourAudioTourLocated")}
                subtitle={t("indicateTheCountryAndCityWhereYourAudioTourIsLocated")}
                layout="location"
            />

            <div className={stepStyles.fieldStack}>
                <Dropdown
                    label=""
                    options={data.locationCountryOptions}
                    onChange={(value: string) => {
                        updateTrackData("country", value);
                        setSelectedCountry(value);
                    }}
                    value={trackData.country}
                    loading={loading}
                    error={error ? true : false}
                    isWithoutMargin
                />

                {selectedCountry && (
                    <Dropdown
                        label=""
                        options={filteredCities}
                        onChange={(value: string) => {
                            updateTrackData("city", value);
                        }}
                        value={trackData.city}
                        disabled={!selectedCountry}
                        loading={loading}
                        error={error ? true : false}
                        isWithoutMargin
                    />
                )}

                {selectedCountry && trackData.city && (
                    <TextInput
                        placeholder={t("enterTheAddress")}
                        value={trackData.startingAddress ? trackData.startingAddress : ""}
                        onChange={(e) => updateTrackData("startingAddress", e.target.value)}
                        size="big"
                        numberOfLines={3}
                    />
                )}

                {selectedCountry && trackData.city && (
                    <TextInput
                        placeholder={t("enterCoordinatesLatLng")}
                        value={coordinates}
                        onChange={(e) => updateCoordinatesStartingPoint(e.target.value)}
                        size="medium"
                    />
                )}
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("continue")}
                    onClick={navigateToNext}
                    disabled={!trackData.city}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>
        </div>
    );
};

export default LocationScreen;
